import { Divider, Grid, List, Typography, useEventCallback } from '@mui/material';
import { DocumentCivilStatusMarriageCrumbs } from 'civil-status/constants/breadcrumb';
import { UploadForm } from 'civil-status/view-models/file-extended';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { DateTime } from 'luxon';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { Cropper } from 'shell/components/shared/cropper';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { DocumentMarriageList } from 'shell/models/document-marriage';
import { HttpResponse } from 'shell/models/http-response';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { ConvertedImage } from 'shell/models/convert-image';

export default function DocumentMarriageUploadPage(): React.ReactElement {
  const navigate = useNavigate();

  const params = useParams();

  const { userProfile } = useUserProfile();

  const [marriage] = useHttpFetch<HttpResponse<DocumentMarriageList>, DocumentMarriageList>(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/${params.anno}-${params.numeroRegistro}-${params.numeroAtto}-${params.parte}-${params.serie1}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
      transformer: (response) => {
        const item = response.data?.payload;
        return item;
      },
    }
  );

  const unlockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/sblocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const data = marriage.data as DocumentMarriageList;

  useEffect(() => {
    return () => {
      if (params) {
        unlockDocument({
          data: {
            key: {
              numeroAtto: params.numeroAtto,
              numeroRegistro: params.numeroRegistro,
              parte: params.parte,
              serie1: params.serie1,
              anno: params.anno,
            },
          },
        });
      }
    };
  }, []);

  const [insertMode, setInsertMode] = useState(false);

  const uploadDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}/atti-matrimonio-sima/caricamento-atto-matrimonio`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const handleUpload = useCallback(
    (values: UploadForm) => {
      (
        uploadDocument({
          data: {
            attoMatrimonioForUpload: {
              numeroRegistro: data?.numeroRegistro,
              anno: data?.anno,
              numeroAtto: data?.numeroAtto,
              parte: data?.parte,
              serie: params.serie1,
              matricola1: data?.matricolaMarito,
              cognome1: data?.cognomeMarito,
              nome1: data?.nomeMarito,
              matricola2: data?.matricolaMoglie,
              cognome2: data?.cognomeMoglie,
              nome2: data?.nomeMoglie,
              idOperatore: userProfile.datiLogin?.key?.userName,
            },
            filename: values.file?.name,
            mimeType: values.file?.type,
            file: values.rawTiff?.split(',')[1],
          },
        }) as ResponsePromise<void>
      ).then(() => {
        toast.success('Operazione eseguita con successo');
        navigate('/stato-civile/matrimonio');
      });
    },
    [uploadDocument, data, navigate]
  );

  const formContext = useFormik<UploadForm>({
    initialValues: {
      file: undefined,
      raw: [],
      rawTiff: '',
    },
    onSubmit: handleUpload,
  });

  const convertDocument = useHttpClient<HttpResponse<ConvertedImage>>(
    `${__CIVIL_STATUS_API_URL__}/convert-image`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};
      (
        convertDocument({
          data: {
            formato: 'png',
            immagineIniziale: (result as string)?.split(',')[1],
          },
        }) as ResponsePromise<HttpResponse<ConvertedImage>>
      ).then((response) => {
        formContext.setValues({
          file,
          raw: response?.data?.payload?.immagineConvertita
            ? (response?.data?.payload?.immagineConvertita as Array<string>)?.map(
                (immagine: string) => `data:image/png;base64,${immagine}`
              )
            : [],
          rawTiff: result as string,
        });
      });
    });

    reader.readAsDataURL(file);
  });

  return (
    <>
      <Breadcrumbs items={DocumentCivilStatusMarriageCrumbs} base="stato-civile" />
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Caricamento Atto
      </Typography>
      <form onSubmit={formContext.handleSubmit}>
        <Grid container spacing={2}>
          {insertMode ? (
            <Grid item xs={12} md={4}>
              Carica documento&nbsp;
              <PmiInputFile
                accept="image/*"
                multiple={false}
                file={formContext.values.file}
                onChange={handleAddFile}
                color="cdmBrandThird"
                tipMaxSize={300}
              />
              <div className="pmi-d-flex pmi-justify-content-end pmi-pt-4">
                <div>
                  <PmiButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => setInsertMode(false)}
                  >
                    Indietro
                  </PmiButton>
                </div>
                <div className="pmi-pl-3">
                  <PmiButton variant="contained" color="primary" type="submit">
                    Carica
                  </PmiButton>
                </div>
              </div>
            </Grid>
          ) : (
            <Grid item xs={12} md={4}>
              <span>Anno: {data?.anno || ''}</span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Registro: {data?.numeroRegistro || ''}
              </span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Atto: {data?.numeroAtto || ''}
              </span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Parte: {data?.parte || ''}
              </span>
              <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                Serie: {data?.serie1 || ''}
                {data?.serie2 || ''}
              </span>
              <Typography
                color="cdmBrandThird.main"
                style={{
                  fontSize: '26px',
                }}
              >
                Atto di matrimonio
              </Typography>
              <div className="pmi-pt-3">
                <span>Data di Matrimonio</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.dataMatrimonio
                    ? DateTime.fromISO(data?.dataMatrimonio).toFormat('dd/MM/yyyy')
                    : ''}
                </span>
              </div>
              <div className="pmi-pt-3">
                <span>Comune di Matrimonio</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneMatrimonio || ''}
                </span>
              </div>
              <div>
                <Divider />
              </div>
              <div className="pmi-pt-3">
                <span>Matricola</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.matricolaMarito || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Cognome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.cognomeMarito || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Nome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.nomeMarito || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Sesso</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.sessoMarito || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Data di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.dataNascitaMarito
                    ? DateTime.fromISO(data?.dataNascitaMarito).toFormat('dd/MM/yyyy')
                    : ''}
                </span>
                <List>
                  <li className="pmi-pl-2">Anni {data?.anniMarito || ''}</li>
                  <li className="pmi-pl-2">
                    Anno nascita{' '}
                    {data?.dataNascitaMarito
                      ? DateTime.fromISO(data?.dataNascitaMarito).toFormat('yyyy')
                      : ''}
                  </li>
                </List>
              </div>
              <div className="pmi-pt-1">
                <span>Comune di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneNascitaMarito || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Comune di residenza</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneResidenzaMarito || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Stato civile</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.statoCivileMarito || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Cognome post Matrimonio</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.cognomeDopoMatrimonioMarito || ''}
                </span>
              </div>
              <div>
                <Divider />
              </div>
              <div className="pmi-pt-3">
                <span>Matricola</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.matricolaMoglie || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Cognome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.cognomeMoglie || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Nome</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.nomeMoglie || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Sesso</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.sessoMoglie || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Data di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.dataNascitaMoglie
                    ? DateTime.fromISO(data?.dataNascitaMoglie).toFormat('dd/MM/yyyy')
                    : ''}
                </span>
                <List>
                  <li className="pmi-pl-2">Anni {data?.anniMoglie || ''}</li>
                  <li className="pmi-pl-2">
                    Anno nascita{' '}
                    {data?.dataNascitaMoglie
                      ? DateTime.fromISO(data?.dataNascitaMoglie).toFormat('yyyy')
                      : ''}
                  </li>
                </List>
              </div>
              <div className="pmi-pt-1">
                <span>Comune di Nascita</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneNascitaMoglie || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Comune di residenza</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.comuneResidenzaMoglie || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Stato civile</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.statoCivileMoglie || ''}
                </span>
              </div>
              <div className="pmi-pt-1">
                <span>Cognome post Matrimonio</span>
                <span className="pmi-d-block pmi-d-md-inline pmi-pl-md-2">
                  {data?.cognomeDopoMatrimonioMoglie || ''}
                </span>
              </div>
              <div className="pmi-d-flex pmi-justify-content-end pmi-pt-4">
                <div>
                  <PmiButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate('/stato-civile/matrimonio')}
                  >
                    Indietro
                  </PmiButton>
                </div>
                <div className="pmi-pl-3">
                  <PmiButton
                    variant="contained"
                    color="primary"
                    onClick={() => setInsertMode(true)}
                  >
                    Inserisci
                  </PmiButton>
                </div>
              </div>
            </Grid>
          )}

          <Grid item xs={12} md={8}>
            <StickyDiv>
              <Cropper
                images={formContext.values.raw || []}
                disabled={!formContext.values?.raw?.length}
              />
            </StickyDiv>
          </Grid>
        </Grid>
      </form>
    </>
  );
}

DocumentMarriageUploadPage.displayName = 'home page component';
